import React, { useCallback, useMemo } from 'react';
import { CapaQuotationPartnerStrategyFile } from '@generated/graphql';
import { SwiperSlide } from 'swiper/react';
import { getYoutubeVideoIdByUrl } from '@utils/youtube';
import { getFileUrl } from '@utils/urls';
import { colorSet } from '@components/Provider';
import { Box } from '@material-ui/core';

const useMediaSwiper = () => {
  const useSwipeSlides = (
    files: CapaQuotationPartnerStrategyFile[],
    className: string
  ) =>
    useMemo(() => {
      return (files as CapaQuotationPartnerStrategyFile[]).map(
        (
          { fileName, fileId, fileExtension, videoThumbnailFileId, youtubeURL },
          index
        ) => {
          const videoExt = [
            'mov',
            'mp4',
            'avi',
            'wmv',
            'mwa',
            'asf',
            'mpg',
            'mpeg',
            'webm'
          ];
          const fileType: 'image' | 'video' = videoExt.includes(
            fileExtension?.toLocaleLowerCase() as string
          )
            ? 'video'
            : 'image';
          if (youtubeURL) {
            const youtubeId = getYoutubeVideoIdByUrl(youtubeURL);
            return (
              <SwiperSlide key={index} className={className}>
                <Box
                  className={`yt-iframe-indicator-${index}`}
                  position={'absolute'}
                  left={57}
                  width={'calc(100% - 114px)'}
                  height={'100%'}
                  bgcolor={colorSet.gray300}
                />
                <iframe
                  title={`${youtubeId}-yt-iframe`}
                  src={`https://www.youtube.com/embed/${youtubeId}?enablejsapi=1`}
                  width="100%"
                  height="100%"
                  allow="autoplay"
                  loading="lazy"
                  onLoad={({ target }) => {
                    const $indicator = (target as Element).parentElement?.querySelector(
                      `.yt-iframe-indicator-${index}`
                    );
                    if ($indicator) {
                      (($indicator as unknown) as ElementCSSInlineStyle).style.display =
                        'none';
                    }
                  }}
                />
              </SwiperSlide>
            );
          }
          return fileType === 'image' ? (
            <SwiperSlide key={index} className={className}>
              <img
                src={getFileUrl(fileId ?? '')}
                alt={fileName ?? fileId ?? ''}
              />
            </SwiperSlide>
          ) : (
            <SwiperSlide key={index} className={className}>
              <video
                controls
                preload="none"
                poster={getFileUrl(videoThumbnailFileId ?? '')}
              >
                <source src={getFileUrl(fileId ?? '')} />
                비디오 로드 오류
              </video>
            </SwiperSlide>
          );
        }
      );
    }, [files, className]);

  const handleStopVideo = useCallback((element: Element) => {
    const target =
      element.querySelector('video') || element.querySelector('iframe');

    // 이미지
    if (!target) {
      return;
    }
    // 비디오
    if ('pause' in target) {
      target.pause();
      return;
    }
    // 유튜브
    if (target.contentWindow) {
      handleIframePointerEvent(element, 'none');
      target.contentWindow.postMessage(
        `{"event":"command","func":"pauseVideo","args":""}`,
        '*'
      );
    }
    //eslint-disable-next-line
  }, []);

  const handleIframePointerEvent = useCallback(
    (element: Element, style: string) => {
      if (element) {
        const target = element.querySelector('iframe');

        if (!target) {
          return;
        }
        target.style.pointerEvents = style;
      }
      return;
    },
    []
  );

  return {
    useSwipeSlides,
    handleIframePointerEvent,
    handleStopVideo
  };
};

export default useMediaSwiper;
